import { Form, Formik } from "formik";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import * as Yup from "yup";
import SideDrawer from "@/components/ui/SideDrawer";
import WelcomeView from "@/components/ui/WelcomeView";
import { menuService } from "@/services/breeze/MenuService";
import { CreateUpdateMenu, Menu } from "@/types/breeze/Menu";
import InputField from "@/components/form/InputField";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { useSearchParams } from "react-router-dom";
import SplitView from "@/components/ui/SplitView";
import { useRoles } from "@/hooks/useCommonQueries";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import SelectField from "@/components/form/SelectField";
import Surface from "@/components/ui/Surface";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { Box, Button, Flex, Text } from "@radix-ui/themes";
import PageTemplate from "@/components/page-template/PageTemplate";
import MenuEditor from "@/components/menu-editor/MenuEditor";
import { useAuth } from "@/providers/AuthProvider";
import { SortableList, SortableItem, DragHandle } from "@/components/dnd";

const MenuPage = withTenantAdminRequired(function MenuPage() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [triggerAddMenuItem, setTriggerAddMenuItem] = useState(false);

  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { currentTenant, safeNavigate } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedMenuId = searchParams.get("menuId");

  const { data: myMenu } = useQuery({
    queryKey: ["my-menu"],
    queryFn: () => menuService.getMyMenu(),
  });

  const {
    data: menus,
    isLoading,
    error,
  } = useQuery<Menu[]>({
    queryKey: ["menus"],
    queryFn: () => menuService.getAllMenus(),
  });

  const {
    data: roles,
    isLoading: isRolesLoading,
    error: rolesError,
  } = useRoles();

  const handleMenuSelect = useCallback(
    (menuId: number) => {
      safeNavigate(`/org/${currentTenant?.id}/settings/menu?menuId=${menuId}`);
    },
    [safeNavigate, currentTenant?.id],
  );

  // Select first menu by default
  useEffect(() => {
    if (menus?.length && !selectedMenuId && myMenu) {
      handleMenuSelect(myMenu.id);
    }
  }, [menus, selectedMenuId, handleMenuSelect, myMenu]);

  const createMenu = useMutation({
    mutationFn: (data: CreateUpdateMenu) => menuService.createMenu(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menus"] });
      setIsDrawerOpen(false);
    },
  });

  const updateMenu = useMutation({
    mutationFn: (data: CreateUpdateMenu) =>
      menuService.updateMenu(editingMenu!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menus"] });
      setIsDrawerOpen(false);
    },
  });

  const deleteMenu = useMutation({
    mutationFn: (id: number) => menuService.deleteMenu(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menus"] });
      safeNavigate(`/org/${currentTenant?.id}/settings/menu`);
    },
  });

  const reorderMenus = useMutation({
    mutationFn: (updates: { menu_id: number; sort_order: number }[]) =>
      menuService.reorderMenus(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menus"] });
      queryClient.invalidateQueries({ queryKey: ["my-menu"] });
    },
  });

  const handleReorderMenus = useCallback(
    (reorderedMenus: Menu[]) => {
      const updates = reorderedMenus.map((menu, index) => ({
        menu_id: menu.id,
        sort_order: index,
      }));
      reorderMenus.mutate(updates);
    },
    [reorderMenus],
  );

  const handleDeleteMenu = async (menu: Menu) => {
    const confirmed = await confirm({
      title: "Delete Menu",
      message: `Are you sure you want to delete "${menu.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      deleteMenu.mutate(menu.id);
    }
  };

  const handleEditMenu = (menu: Menu) => {
    setEditingMenu(menu);
    setIsDrawerOpen(true);
  };

  if (isLoading || isRolesLoading) return <LoadingScreen fullPage={false} />;
  if (error || rolesError) return <div>Error loading menus</div>;
  if (!menus) return null;

  return (
    <PageTemplate
      title="Menu Configuration"
      description={
        <>
          Create and manage your dashboard&apos;s navigation.
          <br />
          Customise your sidebar to provide your users with the most relevant
          links based on their role.
        </>
      }
    >
      <SplitView>
        <SplitView.Left>
          <Box style={{ paddingLeft: "0", paddingRight: "1rem" }}>
            <Flex justify="between" align="center" mb="4">
              <Text size="4" weight="bold">
                Menus
              </Text>
              <Button onClick={() => setIsDrawerOpen(true)} size="1">
                <Plus style={{ width: "16px", height: "16px" }} />
                Add Menu
              </Button>
            </Flex>

            {menus.length === 0 ? (
              <Text color="gray">No menus found</Text>
            ) : (
              <SortableList
                items={[...menus].sort((a, b) => a.sort_order - b.sort_order)}
                getItemId={(menu) => menu.id}
                onReorder={handleReorderMenus}
                renderItem={(menu) => (
                  <SortableItem key={menu.id} id={menu.id}>
                    {({ dragHandleProps }) => (
                      <Surface
                        preTitleContent={<DragHandle {...dragHandleProps} />}
                        onClick={() => handleMenuSelect(menu.id)}
                        selected={selectedMenuId === menu.id.toString()}
                        title={menu.name}
                        body={`${menu.items.length} items - ${menu.role?.name}`}
                        actions={[
                          {
                            label: "Edit",
                            icon: (
                              <Pencil style={{ width: "1rem", height: "1rem" }} />
                            ),
                            onClick: () => {
                              handleEditMenu(menu);
                            },
                          },
                          {
                            label: "Delete",
                            icon: (
                              <Trash2
                                style={{
                                  width: "1rem",
                                  height: "1rem",
                                  color: "var(--red-9)",
                                }}
                              />
                            ),
                            onClick: async () => {
                              await handleDeleteMenu(menu);
                            },
                          },
                        ]}
                      />
                    )}
                  </SortableItem>
                )}
                renderDragOverlay={(menu) => (
                  <Surface
                    title={menu.name}
                    body={`${menu.items.length} items - ${menu.role?.name}`}
                  />
                )}
              />
            )}
          </Box>
        </SplitView.Left>

        <SplitView.Right>
          {selectedMenuId ? (
            <Box>
              {(() => {
                const selectedMenu = menus.find(
                  (m) => m.id.toString() === selectedMenuId,
                );
                return selectedMenu && selectedMenu.items.length === 0 ? (
                  <WelcomeView
                    title={`Add Items to ${selectedMenu.name}`}
                    description="This menu is ready for content! Add navigation items to create a customised experience for your users. Link to entities, external pages, or create nested menu structures."
                    quickStartSteps={[
                      {
                        text: "Add links to your data entities for quick access",
                      },
                      {
                        text: "Create custom navigation to external resources",
                      },
                      {
                        text: "Build nested menu structures for better organisation",
                      },
                      { text: "Set permissions to control who sees each item" },
                    ]}
                    primaryAction={{
                      text: "Add Your First Menu Item",
                      icon: <Plus style={{ width: "16px", height: "16px" }} />,
                      onClick: () => setTriggerAddMenuItem(true),
                    }}
                    secondaryAction={{
                      text: "View Menu Documentation",
                      url: "https://docs.anythink.cloud/navigation",
                    }}
                    padding={{
                      top: "0",
                      left: "var(--space-6)",
                    }}
                  />
                ) : null;
              })()}
              <MenuEditor
                key={selectedMenuId}
                triggerAddItem={triggerAddMenuItem}
                onAddItemTriggered={() => setTriggerAddMenuItem(false)}
                hideWhenEmpty={true}
              />
            </Box>
          ) : (
            <Flex align="center" justify="center" style={{ height: "100%" }}>
              <Text color="gray">Select a menu to edit</Text>
            </Flex>
          )}
        </SplitView.Right>

        <SideDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setEditingMenu(null);
          }}
          title={editingMenu ? `Edit ${editingMenu.name}` : "Create New Menu"}
        >
          <Box p="4">
            <Formik
              initialValues={{
                name: editingMenu?.name || "",
                role_id: editingMenu?.role_id || 0,
              }}
              enableReinitialize
              onSubmit={(values: CreateUpdateMenu) => {
                if (editingMenu) {
                  updateMenu.mutate(values);
                } else {
                  createMenu.mutate(values);
                }
              }}
              validationSchema={Yup.object({
                name: Yup.string().required("Required"),
                role_id: Yup.number().required("Required").min(1, "Required"),
              })}
            >
              <Form>
                <Flex direction="column" gap="4">
                  <InputField name="name" label="Menu Name" required />

                  <SelectField
                    name="role_id"
                    label="Role"
                    required
                    type="number"
                  >
                    {roles?.map((role) => (
                      <SelectField.Item key={role.id} value={role.id}>
                        {role.name}
                      </SelectField.Item>
                    ))}
                  </SelectField>

                  <Box pt="4">
                    <Button
                      type="submit"
                      loading={
                        editingMenu
                          ? updateMenu.isPending
                          : createMenu.isPending
                      }
                    >
                      {editingMenu ? "Update Menu" : "Create Menu"}
                    </Button>
                  </Box>
                </Flex>
              </Form>
            </Formik>
          </Box>
        </SideDrawer>
      </SplitView>
    </PageTemplate>
  );
});

export default MenuPage;
